/**
 * PlotView component - Display plot artifacts in ArtifactPanel
 *
 * Features:
 * - Uses PlotlyChart component for visualization
 * - Handles both data_url (backend-generated) and data (inline) plots
 * - Full-size view option
 * - Clean card-based layout
 */

import PlotlyChart from '../PlotlyChart'
import type { Artifact } from '../../types/chat'

interface PlotViewProps {
  artifacts: Artifact[]
}

function PlotView({ artifacts }: PlotViewProps) {
  if (artifacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-dark-400">
        <div className="text-center">
          <p className="text-sm">No plot artifacts yet</p>
          <p className="text-xs mt-2 text-dark-500">
            Plots will appear here as the agent generates visualizations
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {artifacts.map((artifact, index) => (
        <div
          key={index}
          className="border border-dark-200 rounded-lg overflow-hidden bg-dark-50"
        >
          {/* Title header */}
          <div className="px-4 py-3 bg-dark-100 border-b border-dark-200">
            <h3 className="text-sm font-medium text-dark-700">{artifact.title}</h3>
          </div>

          {/* Plot content */}
          <div className="p-4">
            {artifact.data_url ? (
              <PlotlyChart
                dataUrl={artifact.data_url}
                title={artifact.title}
                fullSize={true}
              />
            ) : artifact.data ? (
              <div className="text-sm text-dark-500">
                <p className="mb-2 text-dark-600">Inline plot data:</p>
                <pre className="text-xs bg-dark-100 p-3 rounded max-h-60 overflow-y-auto">
                  {JSON.stringify(artifact.data, null, 2)}
                </pre>
                <p className="mt-2 text-xs text-dark-400 italic">
                  Note: Inline plot data requires backend rendering. Use data_url for visualizations.
                </p>
              </div>
            ) : (
              <div className="text-sm text-dark-400 italic text-center py-8">
                No plot data available
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default PlotView
